import { createFileRoute } from "@tanstack/react-router";
import { TERM_RULE, SCOPE_EXCLUSION, NO_RELIANCE, LIABILITY_CAP, CLIENT_INDEMNITY } from "@/data/terms";
import { canonicalLinks, ogUrl, ogImage, jsonLd, breadcrumbSchema, webPageSchema } from "@/lib/seo";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service for Website and Engagements | Ajility" },
      {
        name: "description",
        content: "Terms governing the Ajility website, enquiry forms and fractional COO engagements: consumer law rights, liability, intellectual property and cancellation.",
      },
      { property: "og:title", content: "Terms of Service for Website and Engagements | Ajility" },
      { property: "og:description", content: "Terms governing the Ajility website, enquiry forms and fractional COO engagements: consumer law rights, liability, intellectual property and cancellation." },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Terms of Service for Website and Engagements | Ajility" },
      { name: "twitter:description", content: "Terms governing the Ajility website, enquiry forms and fractional COO engagements: consumer law rights, liability, intellectual property and cancellation." },
      ...ogImage("terms", "Ajility terms of service"),
      ogUrl("/terms"),
    ],
    links: canonicalLinks("/terms"),
    scripts: [
      jsonLd(breadcrumbSchema([{ name: "Terms", path: "/terms" }])),
      jsonLd(webPageSchema({ image: "terms",
        path: "/terms",
        name: "Terms of Service for Website and Engagements | Ajility",
        description: "Terms governing the Ajility website, enquiry forms and fractional COO engagements: consumer law rights, liability, intellectual property and cancellation.",
      })),
    ],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <main className="bg-surface pb-24 pt-32">
      <div className="mx-auto max-w-3xl px-6">
        <h1 className="font-serif text-4xl text-midnight md:text-5xl">Terms of Service</h1>
        <p className="mt-4 text-sm text-charcoal">Last updated: 18 August 2026</p>
        <p className="mt-6 text-charcoal">
          These terms govern use of the Ajility website and engagement of Ajility's
          consulting services. By using this site or engaging us, you agree to these
          terms.
        </p>

        <h2 className="mt-10 font-serif text-2xl text-midnight">Who we are</h2>
        <p className="mt-3 text-charcoal">
          Ajility is a trading name of AJILITY PTY LTD, ABN 39 649 782 427. References to
          "we", "us" and "Ajility" in these terms mean that entity. Contact:{" "}
          <a href="mailto:adam@ajility.com.au" className="underline hover:text-midnight-deep">
            adam@ajility.com.au
          </a>
          .
        </p>

        <h2 className="mt-10 font-serif text-2xl text-midnight">Services</h2>
        <p className="mt-3 text-charcoal">
          Ajility provides fractional COO and operational consulting services to
          founder-led businesses. Specific deliverables, fees and timelines are agreed
          in writing before work begins. All fees are quoted plus GST.
        </p>

        <h2 className="mt-10 font-serif text-2xl text-midnight">Scope of our advice</h2>
        <p className="mt-3 text-charcoal">{SCOPE_EXCLUSION}</p>

        <h2 className="mt-10 font-serif text-2xl text-midnight">No reliance</h2>
        <p className="mt-3 text-charcoal">{NO_RELIANCE}</p>

        <h2 className="mt-10 font-serif text-2xl text-midnight">Term and cancellation</h2>
        <p className="mt-3 text-charcoal">
          {TERM_RULE}. Either party may end the engagement at a 90-day boundary by
          giving written notice before that boundary. Fees for work already performed,
          and for the balance of the current quarter, remain payable. One-off work ends
          when the agreed deliverable is provided. Where a written proposal or services
          agreement sets different notice or payment terms, that document applies.
        </p>

        <h2 className="mt-10 font-serif text-2xl text-midnight">Intellectual property</h2>
        <p className="mt-3 text-charcoal">
          All content on this website, and all frameworks, templates, methods and
          materials that Ajility owned before an engagement or develops independently of
          it, remain the property of Ajility. On payment in full, the client owns the
          specific reports, documents and operating artefacts prepared for them under
          the engagement. Ajility grants the client a non-exclusive, perpetual licence
          to use Ajility background materials embedded in those deliverables for the
          client's internal business purposes. Neither party may resell, sublicence or
          publish the other's materials without written consent.
        </p>

        <h2 className="mt-10 font-serif text-2xl text-midnight">Your consumer rights</h2>
        <p className="mt-3 text-charcoal">
          Nothing in these terms excludes, restricts or modifies any guarantee, right or
          remedy you have under the Australian Consumer Law.
        </p>

        <h2 className="mt-10 font-serif text-2xl text-midnight">Liability</h2>
        <p className="mt-3 text-charcoal">{LIABILITY_CAP}</p>

        <h2 className="mt-10 font-serif text-2xl text-midnight">Indemnity</h2>
        <p className="mt-3 text-charcoal">{CLIENT_INDEMNITY}</p>

        <h2 className="mt-10 font-serif text-2xl text-midnight">Causes we list</h2>
        <p className="mt-3 text-charcoal">
          Ajility lists causes on this site as a convenience only. Ajility receives no payment or
          benefit from any listed organisation, does not solicit or receive donations on behalf of
          any organisation, and makes no representation about how any organisation applies funds.
          Listing does not imply endorsement in either direction.
        </p>

        <h2 className="mt-10 font-serif text-2xl text-midnight">Privacy</h2>
        <p className="mt-3 text-charcoal">
          Personal information is handled in line with our{" "}
          <a href="/privacy" className="underline hover:text-midnight-deep">
            privacy policy
          </a>
          .
        </p>

        <h2 className="mt-10 font-serif text-2xl text-midnight">Governing law and jurisdiction</h2>
        <p className="mt-3 text-charcoal">
          These terms are governed by the laws of New South Wales, Australia. Each party
          submits to the non-exclusive jurisdiction of the courts of New South Wales and
          the courts able to hear appeals from them.
        </p>

        <h2 className="mt-10 font-serif text-2xl text-midnight">Contact</h2>
        <p className="mt-3 text-charcoal">
          For questions about these terms, email{" "}
          <a href="mailto:adam@ajility.com.au" className="underline hover:text-midnight-deep">
            adam@ajility.com.au
          </a>
          .
        </p>
      </div>
    </main>
  );
}
